
import { useState, useMemo } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { DriversFilters } from "@/components/drivers/DriversFilters";
import { DriversTable } from "@/components/drivers/DriversTable";
import { DriverCard } from "@/components/drivers/DriverCard";

interface Driver {
  id: string;
  supplier_id: string;
  supervisor_id: string;
  name_ar: string;
  name_en: string;
  mobile: string;
  email: string;
  has_access_permission: boolean;
  suppliers: { name_ar: string };
  employees: { name: string };
  created_at: string;
}

export default function Drivers() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Driver | null>(null);
  const [formData, setFormData] = useState({
    supplier_id: "",
    supervisor_id: "",
    name_ar: "",
    name_en: "",
    mobile: "",
    email: "",
    has_access_permission: false
  });
  const queryClient = useQueryClient();

  const [view, setView] = useState<'cards' | 'table'>('cards');
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("created_at_desc");

  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ['drivers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('drivers')
        .select(`
          *,
          suppliers(name_ar),
          employees(name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Driver[];
    }
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-for-drivers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name_ar')
        .order('name_ar');
      
      if (error) throw error;
      return data;
    }
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees-for-drivers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase
        .from('drivers')
        .insert([data]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success("تم إضافة السائق بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء إضافة السائق");
      console.error(error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase
        .from('drivers')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success("تم تحديث بيانات السائق بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء تحديث بيانات السائق");
      console.error(error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('drivers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success("تم حذف السائق بنجاح");
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء حذف السائق");
      console.error(error);
    }
  });

  const resetForm = () => {
    setFormData({
      supplier_id: "",
      supervisor_id: "",
      name_ar: "",
      name_en: "",
      mobile: "",
      email: "",
      has_access_permission: false
    });
    setEditingItem(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (item: Driver) => {
    setEditingItem(item);
    setFormData({
      supplier_id: item.supplier_id || "",
      supervisor_id: item.supervisor_id || "",
      name_ar: item.name_ar,
      name_en: item.name_en || "",
      mobile: item.mobile || "",
      email: item.email || "",
      has_access_permission: item.has_access_permission || false
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm("هل أنت متأكد من حذف هذا السائق؟")) {
      deleteMutation.mutate(id);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSortBy("created_at_desc");
  };

  const filteredAndSortedDrivers = useMemo(() => {
    let result = [...drivers];

    if (searchTerm) {
      result = result.filter(driver =>
        driver.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (driver.name_en && driver.name_en.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    switch (sortBy) {
      case 'name_ar_asc':
        result.sort((a, b) => a.name_ar.localeCompare(b.name_ar));
        break;
      case 'name_ar_desc':
        result.sort((a, b) => b.name_ar.localeCompare(a.name_ar));
        break;
      case 'created_at_desc':
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
    }

    return result;
  }, [drivers, searchTerm, sortBy]);


  if (isLoading) {
    return <div className="flex justify-center p-8">جاري التحميل...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold">أسماء السائقين</h1>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          if (!open) resetForm();
          setIsDialogOpen(open);
        }}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}>
              <Plus className="w-4 h-4 ml-2" />
              إضافة سائق جديد
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? "تعديل بيانات السائق" : "إضافة سائق جديد"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="supplier_id">المورد</Label>
                  <Select value={formData.supplier_id} onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر المورد" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name_ar}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="supervisor_id">المشرف</Label>
                  <Select value={formData.supervisor_id} onValueChange={(value) => setFormData({ ...formData, supervisor_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر المشرف" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          {employee.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name_ar">الاسم بالعربية</Label>
                  <Input
                    id="name_ar"
                    value={formData.name_ar}
                    onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                    required
                    placeholder="اسم السائق"
                  />
                </div>
                <div>
                  <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
                  <Input
                    id="name_en"
                    value={formData.name_en}
                    onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                    placeholder="Driver name in English"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mobile">الموبايل</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="+966xxxxxxxxx"
                  />
                </div>
                <div>
                  <Label htmlFor="email">البريد الإلكتروني</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="driver@example.com"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="has_access_permission"
                  checked={formData.has_access_permission}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_access_permission: checked })}
                />
                <Label htmlFor="has_access_permission">لديه صلاحيات دخول</Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  إلغاء
                </Button>
                <Button type="submit">
                  {editingItem ? "تحديث" : "إضافة"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <DriversFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onClearFilters={handleClearFilters}
      />
      
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          عرض {filteredAndSortedDrivers.length} من {drivers.length} سائق
        </div>
        <ViewToggle view={view} onViewChange={setView} />
      </div>

      {view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAndSortedDrivers.map((driver) => (
            <DriverCard 
              key={driver.id} 
              driver={driver} 
              onEdit={handleEdit} 
              onDelete={handleDelete} 
            />
          ))}
        </div>
      ) : (
        <DriversTable
          drivers={filteredAndSortedDrivers}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
